import { tokenManager } from '@/lib/tokenManager'
import type { ChatCitation, ChatMessagePart, ChatThreadVisibility } from './types'

export type StreamEvent =
  | { type: 'meta'; thread_id: string; user_message_id: string }
  | { type: 'text'; delta: string }
  | { type: 'tool_call_started'; id: string; name: string }
  | {
      type: 'tool_result'
      id: string
      name: string
      ok: boolean
      summary?: string
      parts?: ChatMessagePart[]
      citations?: ChatCitation[]
    }
  | { type: 'usage'; total_tokens: number }
  | {
      type: 'done'
      reply?: string
      parts?: ChatMessagePart[]
      citations?: ChatCitation[]
      usage?: { total_tokens: number }
      model?: string
    }
  | {
      type: 'persisted'
      thread_id: string
      user_message_id: string
      message_id: string
    }
  | { type: 'error'; message: string; detail?: string }

export interface StreamChatPayload {
  message: string
  thread_id?: string
  agent_id?: string
  // Visibility hint for the auto-created thread on the very first
  // message of a new chat. Server ignores these fields when thread_id
  // is supplied (the thread already has a fixed visibility).
  visibility?: ChatThreadVisibility
  shared_role_ids?: number[]
  shared_user_ids?: number[]
}

export async function streamChat(
  payload: StreamChatPayload,
  onEvent: (evt: StreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const token = tokenManager.get()
  const res = await fetch('/api/chatbot/stream', {
    method: 'POST',
    credentials: 'same-origin',
    signal,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  })

  if (res.status === 402) {
    let body: unknown = {}
    try { body = await res.json() } catch { /* ignore */ }
    const msg = (body as { reply?: string }).reply || 'Monthly token limit reached.'
    onEvent({ type: 'error', message: msg })
    return
  }

  if (!res.ok || !res.body) {
    let detail = ''
    try { detail = await res.text() } catch { /* ignore */ }
    onEvent({
      type: 'error',
      message: `Stream failed (HTTP ${res.status}).`,
      detail: detail.slice(0, 500),
    })
    return
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  for (;;) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    let sep: number
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, sep)
      buffer = buffer.slice(sep + 2)
      for (const line of frame.split('\n')) {
        if (!line.startsWith('data:')) continue
        const payloadStr = line.slice(5).trim()
        if (!payloadStr) continue
        try {
          onEvent(JSON.parse(payloadStr) as StreamEvent)
        } catch {
          /* skip malformed frames */
        }
      }
    }
  }
}
